import styled from '@emotion/styled';
import first from 'lodash/first';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { RouteComponentProps } from 'react-router-dom';
import { toastNotificationsService } from 'src/config/rootService';
import * as Sentry from '@sentry/browser';

import { Dispatch } from 'src/config/rootStore';
import { TopicOfFocusNode, TopicSelectionGroupSelectionType } from 'src/generated/webapp_gql';
import { useGetTopicsOfFocusQuery } from 'src/modules/goals/documents/getTopicsOfFocusQuery.generated';
import { useSelectTopicsOfFocusMutation } from 'src/modules/goals/documents/selectTopicsOfFocusMutation.generated';
import { getUserInformation } from 'src/modules/user/selectors/userSelectors';
import { mediaQueries } from 'src/shared/styles/variables';

import { mediaQuery } from '@modernhealth/vital-foundation';
import {
    DeprecatedButtonGroup,
    BodyTextMedium,
    HeadingText50,
    pxToRem,
} from '@modernhealth/vital-components';
import { BrandWrapper } from '../components/BrandWrapper';
import { CategorySelectBox } from '../components/CategorySelectBox';
import { useChangeAccountTopicsMutation } from '../documents/changeAccountTopicsMutation.generated';
import { useTopicSelectionGroupsQuery } from 'src/modules/user/documents/topicSelectionGroupsQuery.generated';
import { OnboardingCategoriesStepPageTestId } from './testIds';
import { topicSelectionPaths } from 'src/shared/hooks/useOnboardingGuard';

const Wrapper = styled.div({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    position: 'absolute',
    top: '20%',
    textAlign: 'center',
    width: '100%',
    padding: '1em 1em 4em',
    [mediaQueries.sm]: {
        top: '8%',
    },
});

const TextsWrapper = styled.div({
    position: 'relative',
    display: 'inline-block',
    padding: '0 15px',
});

const ContentWrapper = styled.div({
    width: '30%',
    marginTop: pxToRem(40),

    [mediaQuery.maxWidth('md')]: {
        minWidth: '90%',
    },
    [mediaQuery.minWidth('md')]: {
        minWidth: pxToRem(450),
    },
});

const NewCategoryListWrapper = styled.div({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'column',
    marginBottom: pxToRem(24),
});

export interface MatchParams {
    providerSelectionMode: TopicSelectionGroupSelectionType;
}

interface CustomizedState {
    prevPathName: string;
    loadingText: string;
}

type Props = RouteComponentProps<MatchParams>;

const getKeyByValue = (object, value) => {
    return Object.keys(object).find(key => object[key] === value);
};

export const OnboardingCategoriesStepPage: React.FC<Props> = ({ history, match, location }) => {
    const { t } = useTranslation();

    const dispatch: Dispatch = useDispatch();

    const { careRecommendations } = useSelector(getUserInformation);

    const { data, loading } = useGetTopicsOfFocusQuery({
        fetchPolicy: 'cache-and-network',
    });
    const { data: topicSelectionGroupRes, refetch } = useTopicSelectionGroupsQuery({
        fetchPolicy: 'cache-and-network',
    });
    const [selectTopicsOfFocus] = useSelectTopicsOfFocusMutation();
    const [changeAccountTopics] = useChangeAccountTopicsMutation();

    const [dropDownState, setDropDownState] = useState({});
    const [submitIsInProgress, setSubmitIsInProgress] = useState(false);

    useEffect(() => {
        refetch();
    }, [refetch]);

    useEffect(() => {
        if (!data?.topicsOfFocus) {
            return;
        }

        const obj = {};
        const topicsOfFocus = first(topicSelectionGroupRes?.topicSelectionGroups);

        // map shape: { 'Anxiety or Worry': "d8d596e3-5d69-41bd-85ed-19ae20a86da8", ... }
        const subtopicOfFocusMapUuid = topicsOfFocus?.subtopicSelections?.reduce((acc, item) => {
            if (!item?.subtopicOfFocus?.isActive) {
                return acc;
            }
            const name = item?.subtopicOfFocus?.name;
            const uuid = item?.subtopicOfFocus?.uuid;
            acc[name] = uuid;
            return acc;
        }, {});

        // if suptopicOfFocus name can be looked up in hash return [subtopicOfFocus.uuid]
        const fillPrevSelectedItems = (hash, subtopics) => {
            const selectedItems = subtopics ?? [];
            return selectedItems.reduce((acc, subtopic) => {
                if (hash?.hasOwnProperty(subtopic?.name)) {
                    return [...acc, hash[subtopic?.name]];
                }
                return acc;
            }, []);
        };

        data?.topicsOfFocus.forEach(item => {
            obj[item?.uuid] = {
                active: false,
                name: item?.name,
                id: item?.uuid,
                selectedItems: fillPrevSelectedItems(
                    subtopicOfFocusMapUuid,
                    item?.subtopicsOfFocus,
                ),
            };
        });
        setDropDownState(obj);
    }, [loading, data, topicSelectionGroupRes]);

    const newCategories = Object.keys(dropDownState)
        .filter(key => dropDownState[key].selectedItems.length > 0)
        .map(key => ({
            name: dropDownState[key].name,
            id: dropDownState[key].id,
            subcategories: dropDownState[key].selectedItems,
        }));

    const handleSubmitOnboardingTopicsOfFocus = async () => {
        try {
            const nextTopicSelections = Object.keys(dropDownState)
                .filter(key => {
                    return dropDownState[key].selectedItems.length > 0;
                })
                .map(key => {
                    return {
                        topicUuid: key,
                        subtopicUuids: dropDownState[key].selectedItems,
                    };
                });

            await selectTopicsOfFocus({
                variables: {
                    careRecommendationUuid: careRecommendations[0].uuid,
                    topicSelections: nextTopicSelections,
                },
            });

            // the backend handle mapping the new topics to the old ones so we
            // want to just go refetch them now that they have been updated in the db
            await dispatch.goalInterests.fetchGoalInterests();
            history.push('/onboarding-wellness');
        } catch (e) {
            Sentry.withScope(scope => {
                scope.setExtra(
                    'error_details',
                    "Couldn't save subtopic selections in profile settings",
                );

                Sentry.captureException(e);
            });
        }
    };

    const handleSubmitProfileTopicsOfFocus = async selectionType => {
        try {
            const subtopicUuids = Object.keys(dropDownState).reduce((acc, key) => {
                return [...acc, ...dropDownState[key].selectedItems];
            }, []);

            await changeAccountTopics({
                variables: {
                    args: {
                        subtopicUuids,
                        selectionType,
                    },
                },
            });

            // the backend handle mapping the new topics to the old ones so we
            // want to just go refetch them now that they have been updated in the db
            await dispatch.goalInterests.fetchGoalInterests();

            isUpdatingTopicOfFocus(true);
        } catch (e) {
            Sentry.withScope(scope => {
                scope.setExtra(
                    'error_details',
                    "Couldn't save subtopic selections in profile settings",
                );

                Sentry.captureException(e);
            });
        }
    };

    const isUpdatingTopicOfFocus = (isSubmit: boolean): void => {
        const locationState = location.state as CustomizedState;
        const prevPathName = locationState?.prevPathName;
        const loadingText = isSubmit
            ? t('assessments:wellbeing_report.loading_text.updating')
            : t('assessments:wellbeing_report.loading_text.populating');

        if (prevPathName === '/wellbeing-report') {
            history.push(prevPathName, { loadingText });
        } else {
            history.goBack();
        }
    };

    const handleSubmit = async () => {
        setSubmitIsInProgress(true);
        switch (match?.params?.providerSelectionMode) {
            case topicSelectionPaths[TopicSelectionGroupSelectionType.OnboardingFlow]:
                await handleSubmitOnboardingTopicsOfFocus();
                break;
            case topicSelectionPaths[TopicSelectionGroupSelectionType.ProfileSettings]:
            case topicSelectionPaths[TopicSelectionGroupSelectionType.WellbeingReport]:
                const topicSelectionPathKey = getKeyByValue(
                    topicSelectionPaths,
                    match?.params?.providerSelectionMode,
                );
                await handleSubmitProfileTopicsOfFocus(topicSelectionPathKey);
                break;
            default:
                await handleSubmitOnboardingTopicsOfFocus();
                break;
        }
        setSubmitIsInProgress(false);
    };

    const handleOnClickSelectBox = (id: number) => {
        const next = {};
        Object.keys(dropDownState).map(key => {
            next[key] = {
                ...dropDownState[key],
                active: false,
            };
        });

        setDropDownState({
            ...next,
            [id]: {
                ...dropDownState[id],
                active: !dropDownState[id].active,
            },
        });
    };

    const handleOnClickOutsideSelectBox = (id: number) => {
        setDropDownState({
            ...dropDownState,
            [id]: {
                ...dropDownState[id],
                active: false,
            },
        });
    };

    const handleOnClickSubtopic = (categoryId: number, topic: string) => {
        const isAddingNewCategory = newCategories.find(cat => cat.id === categoryId);
        // validation bc user can't select more than 3 categories
        if (!isAddingNewCategory && newCategories.length === 3) {
            return;
        }

        // this refers to the subtopics a user has selected in the active dropdown
        const currentlySelectedSubTopics = dropDownState[categoryId].selectedItems;

        const everySelectedSubTopic = newCategories.flatMap(cat => cat.subcategories);
        // validation bc user can't select more than 3 subtopics
        if (everySelectedSubTopic.length === 3 && !everySelectedSubTopic.includes(topic)) {
            toastNotificationsService.renderErrorToast(
                t('onboarding:onboarding_categories_step_page.maximum_number_of_topics_selected'),
            );

            return;
        }

        // add or remove the topic a user selected to the selectedItems array for the active dropdown
        const nextTopics = currentlySelectedSubTopics.includes(topic)
            ? currentlySelectedSubTopics.filter(item => item !== topic)
            : [...currentlySelectedSubTopics, topic];

        // update the state of the dropdown
        setDropDownState({
            ...dropDownState,
            [categoryId]: {
                ...dropDownState[categoryId],
                selectedItems: nextTopics,
            },
        });
    };

    const isContinueDisabled = !newCategories.length || submitIsInProgress;

    return (
        <BrandWrapper>
            <Wrapper>
                <TextsWrapper>
                    <HeadingText50>
                        {t('onboarding:onboarding_categories_step_page.title')}
                    </HeadingText50>
                    <BodyTextMedium centered marginTop={6} fontColor="gray65">
                        {t('onboarding:onboarding_categories_step_page.description')}
                    </BodyTextMedium>
                </TextsWrapper>
                <ContentWrapper>
                    <NewCategoryListWrapper
                        data-testid={OnboardingCategoriesStepPageTestId.CategoryList}
                    >
                        {data?.topicsOfFocus &&
                            (data?.topicsOfFocus as TopicOfFocusNode[]).map(category => {
                                return (
                                    <CategorySelectBox
                                        key={category?.uuid}
                                        name={category?.name || ''}
                                        image={category?.image || ''}
                                        active={dropDownState[category?.uuid]?.active}
                                        selectedItems={dropDownState[category?.uuid]?.selectedItems}
                                        subTopics={category?.subtopicsOfFocus}
                                        onClickOutsideBox={() =>
                                            handleOnClickOutsideSelectBox(category?.uuid)
                                        }
                                        onClickBox={() => handleOnClickSelectBox(category?.uuid)}
                                        onClickSubtopic={subtopicUuid =>
                                            handleOnClickSubtopic(category?.uuid, subtopicUuid)
                                        }
                                    />
                                );
                            })}
                    </NewCategoryListWrapper>

                    <DeprecatedButtonGroup
                        onFirstButtonClick={() => isUpdatingTopicOfFocus(false)}
                        onSecondButtonClick={handleSubmit}
                        secondButtonDisabled={isContinueDisabled}
                        firstButtonText={t('common.back')}
                        secondButtonText={t('common.continue')}
                        secondButtonTestId={OnboardingCategoriesStepPageTestId.Continue}
                    />
                </ContentWrapper>
            </Wrapper>
        </BrandWrapper>
    );
};
